import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Album } from '../../shared-api/albums/album.model';
import { AlbumManagerService } from '../../shared-api/albums/album-manager.service';
import { BroadcastService } from '../../core/services/broadcast.service';
import { ActivatedRoute, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { MyApp } from '../../shared-api/common/enumerations';
import { ImageManagerService } from '../../shared-api/images/image-manager.service';
import { MemberManagerService } from '../../shared-api/members/member-manager.service';
import { AuthManagerService } from '../../shared-api/auth/auth-manager.service';
import { Carousel } from 'primeng/carousel';

@UntilDestroy()
@Component({
    selector: 'myapp-photos',
    templateUrl: './photos.component.html',
    styleUrls: [ './photos.component.less' ],
    animations: [
        trigger('fadeTrigger', [
            state('opaque', style({ opacity: 1.0, height: '100px' })),
            state('transparent', style({ opacity: 0.3, height: '0px' })),
            transition('transparent => opaque', animate('300ms ease-in'))
        ]),
        trigger('largeImageTrigger', [
            state('opaque', style({ opacity: 1.0 })),
            state('transparent', style({ opacity: 0.3 })),
            transition('transparent => opaque', animate('300ms ease-in'))
        ]),
    ]
})
export class PhotosComponent implements OnInit, AfterViewInit, OnDestroy
{
    private static CAROUSEL_IMAGE_HEIGHT = 94; // Note: This MUST match the calculated size in the LESS file (height - padding * 2)

    @ViewChild('carouselContainer', { static: false }) carouselContainer: ElementRef;
    @ViewChild('carousel', { static: false }) carousel: Carousel;

    loading = true;
    album: Album;
    currentPhotoIndex = 0;

    isMember = false;
    numberOfImagesThatFitInCarousel = 0;
    numberOfImagesToScroll = 5;
    largeImageState = 'opaque';
    AssetType = MyApp.AssetType;

    private favorites: Album;

    @HostListener('document:keydown', ['$event'])
    keypress(e: KeyboardEvent)
    {
        if (e.key === 'ArrowRight')
        {
            e.stopImmediatePropagation();
            this.onNavigateNext();
            return;
        }
        if (e.key === 'ArrowLeft')
        {
            e.stopImmediatePropagation();
            this.onNavigatePrevious();
            return;
        }
    }

    constructor(private albumManager: AlbumManagerService,
                private authManager: AuthManagerService,
                private broadcastService: BroadcastService,
                private memberManager: MemberManagerService,
                private messageService: MessageService,
                private imageManager: ImageManagerService,
                private sanitizer: DomSanitizer,
                private route: ActivatedRoute,
                private router: Router) { }

    ngOnInit()
    {
        this.isMember = this.authManager.authenticatedUser.isMember;
        this.broadcastService.showHeader.next(true);
        this.route.params.subscribe(params =>
        {
            const albumId = params['id'] == null ? 0 : +params['id'];
            this.currentPhotoIndex = params['index'] == null ? 0 : +params['index'];
            this.loadAlbum(albumId);
        });

        this.broadcastService.windowResize.pipe(untilDestroyed(this)).subscribe(() => this.handleResize());
    }

    ngAfterViewInit(): void
    {
        this.handleResize(); // Ensure the view is initialized first
    }

    ngOnDestroy(): void
    {
    }

    get isFavorite(): boolean {
        if (this.favorites == null || this.favorites.ImageAssets == null) return false;

        return this.favorites.ImageAssets.some(ia => ia.Id === this.album.ImageAssets[this.currentPhotoIndex].Id);
    }

    private loadAlbum(id)
    {
        this.loading = true;
        this.albumManager.favorites().subscribe(favorites =>
        {
            this.favorites = favorites;
            this.albumManager.getAlbum(id, true).subscribe((parentAlbum: Album) =>
            {
                this.album = parentAlbum;
                this.loading = false;
                setTimeout(() => this.handleResize());
            });
        });
    }

    private handleResize()
    {
        if (this.carouselContainer) {
            const containerWidth = this.carouselContainer.nativeElement.offsetWidth - 90;
            this.numberOfImagesThatFitInCarousel = Math.min(Math.floor(containerWidth / 100), this.album.ImageAssets.length);
            this.scrollCarouselToSelectedImage();
        }
    }

    onImageClicked(imageAsset)
    {
        this.currentPhotoIndex = this.album.ImageAssets.findIndex(ia => ia.Id === imageAsset.Id);
        this.scrollCarouselToSelectedImage();
    }

    onBackToGalleryClicked()
    {
        this.router.navigate([ '/folder', 0 ]);
    }

    onBackToAlbumClicked()
    {
        this.router.navigate([ '/album', this.album.Id, this.currentPhotoIndex ]);
    }

    onNavigatePrevious()
    {
        if (this.currentPhotoIndex === 0) return;
        this.currentPhotoIndex--;
        this.scrollCarouselToSelectedImage();
    }

    onNavigateNext()
    {
        if (this.currentPhotoIndex >= this.album.ImageAssets.length - 1) return;
        this.largeImageState = 'transparent';
        this.currentPhotoIndex++;
        setTimeout(() => this.largeImageState = 'opaque');
        this.scrollCarouselToSelectedImage();
    }

    get getSafeVideoUrlForCurrentImage(): SafeUrl
    {
        let mediaUrl = this.album.ImageAssets[this.currentPhotoIndex].MediaURL;
        if (mediaUrl == null) mediaUrl = '';
        if (mediaUrl.length > 0)
        {
            if (mediaUrl.indexOf('vimeo.com') === -1)
            {
                mediaUrl = mediaUrl.replace('watch?v=', 'embed/')
                    .replace('youtu.be/', 'youtube.be/embed/')
                    .replace('&feature=youtu.be', '');
            }
            else
            {
                mediaUrl = `https://player.vimeo.com/video/${this.album.ImageAssets[this.currentPhotoIndex].vimeoVideoId}`;
            }
        }

        const sanitized = this.sanitizer.bypassSecurityTrustResourceUrl(mediaUrl);
        return sanitized;
    }

    onDownloadImage()
    {
        this.imageManager.downloadImageAsset(this.album.ImageAssets[this.currentPhotoIndex])
            .subscribe(result =>
            {
                if (result)
                {
                    this.messageService.add({ severity: 'info', summary: 'Successful', detail: 'The image was successfully downloaded.' });
                }
            });
    }

    onFavoriteClicked()
    {
        if (!this.isFavorite)
        {
            this.memberManager.addFavoriteImage(this.album.ImageAssets[this.currentPhotoIndex].Id).subscribe(result =>
            {
                this.albumManager.favoriteAdded(this.album.ImageAssets[this.currentPhotoIndex]);
            });
            return;
        }

        this.memberManager.deleteFavoriteImage(this.album.ImageAssets[this.currentPhotoIndex].Id).subscribe(result =>
        {
            this.albumManager.favoriteDeleted(this.album.ImageAssets[this.currentPhotoIndex]);
        });
        return;
    }

    scrollCarouselToSelectedImage(): void
    {
        if (this.carousel == undefined || this.album == undefined) return;

        const firstVisibleItem = this.carousel.page * this.numberOfImagesToScroll;
        const lastVisibleItem = firstVisibleItem + this.numberOfImagesThatFitInCarousel - 1;
        if (this.currentPhotoIndex >= firstVisibleItem && this.currentPhotoIndex <= lastVisibleItem) return;

        const pageIndexOfItem = Math.ceil(Math.max(0, this.currentPhotoIndex + 1 - this.numberOfImagesThatFitInCarousel) / this.numberOfImagesToScroll);
        this.carousel.page = pageIndexOfItem;
    }
}
